// Libraries
import React from 'react';

// Supermove
import {Loading, Space, Icon} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {UserModel, InboundWebhookRequest, InboundWebhookDelivery} from '@supermove/models';

// App
import LargeModal from '@shared/design/components/Modal/LargeModal';
import Table from '@shared/design/components/Table';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';

const LeadProviderRequestModal = ({
  inboundWebhookRequestUuid,
  isOpen,
  handleClose,
}: {
  inboundWebhookRequestUuid: string;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const {loading, data} = useQuery<Response>(LeadProviderRequestModal.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      inboundWebhookRequestUuid,
    },
  });

  return (
    <Loading loading={loading} as={PageLoadingIndicator}>
      {() => (
        <Content request={data!.inboundWebhookRequest} isOpen={isOpen} handleClose={handleClose} />
      )}
    </Loading>
  );
};

const Content = ({
  request,
  isOpen,
  handleClose,
}: {
  request: InboundWebhookRequest;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  return (
    <LargeModal
      title={`Request ID: ${request.uuid}`}
      isOpen={isOpen}
      handlePrimaryAction={handleClose}
      primaryActionText={'Close'}
      primaryActionIcon={Icon.Xmark}
      handlePressOutside={handleClose}
      isScrollable
      isResponsive
    >
      <Table
        items={request.headersPretty}
        itemKey={'key'}
        columnDefinitions={[
          {
            headerLabel: 'Request Headers',
            flex: 1,
            cellComponent: (header: {key: string; value: string}) => <pre>{header.key}</pre>,
            mobileOptions: {
              isInHeader: true,
              rank: 0,
            },
          },
          {
            flex: 2,
            cellComponent: (header: {key: string; value: string}) => <pre>{header.value}</pre>,
            mobileOptions: {
              isInHeader: true,
              rank: 1,
            },
          },
        ]}
        hasBorder
        emptyStateText={'No headers on this request.'}
      />
      <Space height={16} />
      <Table
        items={[request]}
        itemKey={'id'}
        columnDefinitions={[
          {
            headerLabel: 'Request Body',
            flex: 1,
            cellComponent: ({bodyPretty}: InboundWebhookRequest) => <pre>{bodyPretty}</pre>,
          },
        ]}
        hasBorder
        emptyStateText={'No body on this request.'}
      />
      <Space height={16} />
      <Table
        items={request.mostRecentDelivery?.deliveredAt ? [request.mostRecentDelivery] : []}
        itemKey={'id'}
        columnDefinitions={[
          {
            headerLabel: 'Response Data',
            flex: 1,
            cellComponent: ({dataPretty}: InboundWebhookDelivery) => <pre>{dataPretty}</pre>,
          },
        ]}
        hasBorder
        emptyStateText={
          request.mostRecentDelivery?.isExpired
            ? 'Request timed out.'
            : 'No response data for this request.'
        }
      />
    </LargeModal>
  );
};

LeadProviderRequestModal.query = gql`
    query OrganizationSettingsLeadProviderRequestModal($inboundWebhookRequestUuid: String!){
        ${gql.query}
        viewer {
            id
            viewingOrganization {
                id
                timezone
            }
        }
        inboundWebhookRequest: inboundWebhookRequest(inboundWebhookRequestUuid: $inboundWebhookRequestUuid) {
            id
            createdAt
            uuid
            headersPretty {
                key
                value
            }
            bodyPretty
            mostRecentDelivery {
                id
                isExpired
                deliveredAt
                dataPretty
            }
        }
    }
`;

interface Response {
  viewer: UserModel;
  inboundWebhookRequest: InboundWebhookRequest;
}

export default LeadProviderRequestModal;
